<script setup lang="ts">
import {
    ref, computed,
} from 'vue';
import { useQuasar } from 'quasar';
import { type FlightSchoolCourseStudent, StudentCourseStatus } from '@/types/FlightSchoolStudent';
import StudentCertificates from '@/components/Student/StudentCertificates.vue';
import CourseLockout from '@/components/Student/CourseLockout.vue';
import type FlightSchool from '@/types/FlightSchool';
import type FlightSchoolCourse from '@/types/FlightSchoolCourse';
import { printableStudentFromCourseStudent } from '@/types/FlightSchoolStudent';
import StatusBadge from '@/components/Utilities/StatusBadge.vue';
import { getDisplayTime, type StudentRatingTiming } from '@/types/Performance';
import { useUserStore } from '@/stores/user';
import CardSegments from '@/components/CardSegments.vue';
import type { CardSegment } from '@/types/CardSegment';

const props = defineProps<{
    student: FlightSchoolCourseStudent,
    school: FlightSchool,
    course: FlightSchoolCourse,
    studentTiming: StudentRatingTiming|null,
    averageQuizScore: number|null,
}>();

const userStore = useUserStore();

const isLockedOut = ref(props.student.lockout !== null && props.student.lockout.lockedOut);
const lockoutReason = ref(props.student.lockout?.reason ?? '');
const $q = useQuasar();
const totalTime = computed((): string => (props.studentTiming?.totalTime ? getDisplayTime(props.studentTiming.totalTime) : '00:00:00'));
const averageScore = props.averageQuizScore !== null ? `${props.averageQuizScore}%` : '--';
const loading = ref(false);

const timeRemaining = computed((): string => {
    if (props.studentTiming !== null) {
        if (props.studentTiming.requiredTime > props.studentTiming.totalTime) {
            return getDisplayTime(props.studentTiming.requiredTime - props.studentTiming.totalTime);
        }
    }

    return '--';
});

const updateLockout = () => {
    isLockedOut.value = !isLockedOut.value;

    if (isLockedOut.value) {
        lockoutReason.value = 'Locked out manually by an administrator.';
    } else {
        lockoutReason.value = '';
    }
};

const cardSegments: CardSegment[] = [
    {
        iconName: 'sym_o_percent',
        description: 'Average Course Score',
        content: averageScore,
    },
    {
        iconName: 'sym_o_hourglass_top',
        description: 'Time Remaining',
        content: timeRemaining,
    },
    {
        iconName: 'sym_o_alarm',
        description: 'Total Course Time',
        content: totalTime,
    },
];

const smallIconClasses = 'q-pr-sm q-pb-xs small-icon';
const cardRowClasses = 'row card-row q-mb-md q-gutter-md';
const alignRight = computed(() => ($q.screen.lt.md ? 'left' : 'right'));

</script>
<template>
    <q-card
        id="student-card"
        flat
    >
        <q-card-section
            class="col"
            align="left"
        >
            <div class="student-name">
                {{ student.firstName }} {{ student.lastName }}
            </div>
        </q-card-section>
        <CardSegments
            :card-segments="cardSegments"
            :class="'q-px-lg'"
        />
        <q-card-section
            class="card-body row col-12"
        >
            <q-card-section
                class="col-md col-12"
            >
                <q-card-section
                    align="left"
                    :class="cardRowClasses"
                >
                    <div class="col-md-3 col-12">
                        <q-icon
                            :class="smallIconClasses"
                            name="sym_o_badge"
                        />
                        <span class="label">Student ID</span>
                    </div>
                    <div class="col-md-2 col-12 labeled-text">
                        <span>{{ student.id }}</span>
                    </div>
                    <div
                        class="col-md col-12"
                        :align="alignRight"
                    >
                        <StudentCertificates
                            :current-school="school"
                            :current-course="course"
                            :printable-student="printableStudentFromCourseStudent(student)"
                            :certificate-type="'enrollment'"
                        />
                    </div>
                </q-card-section>
                <q-card-section
                    align="left"
                    :class="cardRowClasses"
                >
                    <div class="col-md-3 col-12">
                        <q-icon
                            :class="smallIconClasses"
                            name="sym_o_workspace_premium"
                        />
                        <span class="label">Course</span>
                    </div>
                    <div
                        class="col-md-2 col-12 labeled-text"
                    >
                        <span>{{ course.certificate.title }}</span>
                    </div>
                    <div
                        class="col-md col-12"
                        :align="alignRight"
                    >
                        <StudentCertificates
                            :current-school="school"
                            :current-course="course"
                            :printable-student="printableStudentFromCourseStudent(student)"
                            :certificate-type="'graduation'"
                        />
                    </div>
                </q-card-section>
            </q-card-section>
            <q-card-section
                class="col-md col-12"
            >
                <q-card-section
                    align="left"
                    :class="cardRowClasses"
                >
                    <div class="col-md-4 col-12">
                        <q-icon
                            :class="smallIconClasses"
                            name="o_school"
                        />
                        <span class="label">Course Status</span>
                    </div>
                    <div
                        class="col-md-3 col-12 labeled-text"
                    >
                        <div class="row">
                            <div class="col-12">
                                <div class="q-pr-sm q-pb-xs primary-status">
                                    <StatusBadge :status="student.courseStatus" />
                                </div>
                                <span
                                    v-if="isLockedOut"
                                >
                                    <StatusBadge
                                        :status="StudentCourseStatus.locked"
                                        :tooltip="lockoutReason"
                                    />
                                </span>
                            </div>
                        </div>
                    </div>
                    <div
                        class="col-md col-12"
                        :align="alignRight"
                    >
                        <CourseLockout
                            v-if="isLockedOut || userStore.isGlobalAdmin"
                            :school="school"
                            :course="course"
                            :student="student"
                            @update-lockout="updateLockout"
                        />
                    </div>
                </q-card-section>
                <q-card-section
                    align="left"
                    :class="cardRowClasses"
                >
                    <div class="col-md-4 col-12">
                        <q-icon
                            :class="smallIconClasses"
                            name="sym_o_breaking_news_alt_1"
                        />
                        <span class="label">Exam Status</span>
                    </div>
                    <div
                        class="col-md-2 col-12 labeled-text"
                    >
                        <div class="row">
                            <div class="col-12">
                                <div class="q-pr-sm q-pb-xs primary-status">
                                    <StatusBadge :status="student.examStatus" />
                                </div>
                            </div>
                        </div>
                    </div>
                </q-card-section>
            </q-card-section>
        </q-card-section>
        <q-inner-loading
            :showing="loading"
        />
    </q-card>
</template>
<style scoped lang="scss">
#student-card {
    background: var(--fsum-white);
    min-width: fit-content;
    padding: 0 16px;

    @media screen and (max-width: $breakpoint-md-min) {
        padding: 0;

        button#graduation-certificate, button#enrollment-certificate {
            :deep(span.q-btn__content) {
                text-align: left;
                justify-content: left;
                justify-items: left;
            }
        }
        :deep(button#course-lockout span.q-btn__content) {
            text-align: left;
            justify-content: left;
            justify-items: left;
        }
    }
}
.student-name {
    font-size: 36px;
    font-weight: 700;

    @media screen and (max-width: $breakpoint-md-min) {
        padding-left: 16px;
    }
    @media screen and (max-width: $breakpoint-sm-min) {
        font-size: 25px;
    }
}
.student-info {
    font-size: 20px;
}
.card-row {
    border-bottom: 1px solid var(--fsum-gray5);
}
:deep(.small-icon.q-icon) {
    color: var(--fsum-gray2);
    font-size: 20px;
}
span.label {
    font-size: 16px;
    font-weight: bold;
}
div.labeled-text {
    font-size: 16px;
    display: inline-flex;
}
.link-row {
    font-size: 14px;
}
.primary-status {
    display: inline-block;
}
.lockout-info {
    font-size: 14px;
    font-style: italic;
    color: var(--fsum-med-gray);
}
:deep(.q-btn) {
    padding-bottom: 20px;
}
</style>
